import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BatchJobKindBadge } from "../../../components/domain/data-display/BatchJobKindBadge";
import { BatchJobStatusBadge } from "../../../components/domain/data-display/BatchJobStatusBadge";
import { EmptyState } from "../../../components/primitives/feedback/EmptyState";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { PageHeader } from "../../../components/primitives/layout/PageHeader";
import { gaps } from "../../../components/primitives/spacing";
import { Panel } from "../../../components/primitives/surfaces/Panel";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { getWorkQueue } from "../../../lib/corpusApi";
import {
  describeWorkQueueAction,
  type WorkQueueActionView,
  type WorkQueueJobView,
} from "./work_queue_views";

function JobRow({ job }: { job: WorkQueueJobView }) {
  return (
    <Stack direction="row" gap={gaps.snug} alignItems="center" flexWrap="wrap">
      <Link component={RouterLink} to={job.to} variant="body2" underline="hover">
        {job.name}
      </Link>
      <BatchJobKindBadge label={job.kindLabel} />
      <BatchJobStatusBadge status={job.status} />
      {job.owes ? (
        <Typography variant="body2" color="text.secondary">
          {job.owes}
        </Typography>
      ) : null}
    </Stack>
  );
}

function NextActionCard({ view }: { view: WorkQueueActionView }) {
  return (
    <Panel>
      <Stack gap={gaps.snug}>
        <Typography variant="subtitle1" component="h2">
          {view.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {view.description}
        </Typography>
        {view.jobs.map((job) => (
          <JobRow key={job.id} job={job} />
        ))}
        {view.moreJobs > 0 ? (
          <Typography variant="body2" color="text.secondary">
            and {view.moreJobs} more
          </Typography>
        ) : null}
        {view.to && view.linkLabel ? (
          <Button
            component={RouterLink}
            to={view.to}
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
          >
            {view.linkLabel}
          </Button>
        ) : null}
      </Stack>
    </Panel>
  );
}

/** Kodakku's landing page: what the signed-in user has to do next, each item linking to where it is done. */
export default function Home() {
  const { data, loading, error, refresh } = useAsyncData(getWorkQueue);

  return (
    <InternalPage width="full">
      <PageHeader title="Home" subtitle="What needs doing next" />
      {loading ? (
        <LoadingState />
      ) : error || !data ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refresh()}>
              Try again
            </Button>
          }
        >
          {error ?? "Couldn't load what needs doing."}
        </Alert>
      ) : data.actions.length === 0 ? (
        <EmptyState
          title="Nothing pending"
          message="No documents, reviews or failures are waiting on you."
        />
      ) : (
        <Stack gap={gaps.base}>
          {data.actions.map((action) => (
            <NextActionCard key={action.type} view={describeWorkQueueAction(action)} />
          ))}
        </Stack>
      )}
    </InternalPage>
  );
}
